import type { ReactNode } from 'react';
import { PlainLink, type LinkComponent } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { AdminDesktopGpus, DesktopMachine } from './desktop-types';
import { chipLabel, runsWords } from './desktop-words';
import './desktop-strip.css';

/** One machine: a big state dot, its name and card, and what it runs and whether it works now. */
function Chip({ m }: { m: DesktopMachine }): ReactNode {
  const f = useFormat();
  const working = m.working.reduce((n, w) => n + w.inFlight, 0);
  const state =
    m.state === 'offline'
      ? `Offline since ${f.ago(m.lastSeen)}`
      : m.state === 'paused'
        ? 'Paused'
        : 'Online';
  return (
    <li className={`ds-chip ds-chip--${m.state}`}>
      <span className="ds-chip__dot" aria-hidden="true" />
      <span className="ds-chip__text">
        <strong className="ds-chip__name">{chipLabel(m)}</strong>
        <span className="ds-chip__state">
          {state}
          {m.state === 'offline'
            ? ''
            : ` · ${runsWords(m)}${working > 0 ? `, working now (${f.int(working)})` : ', idle'}`}
        </span>
      </span>
    </li>
  );
}

/**
 * The desktop GPUs at a glance, at the top of Stages and queues and Health: one chip a machine with a big green,
 * amber or red dot (online, paused, offline since when), what it runs and whether it works now, and a grey chip a
 * machine enrolled that never connected. The whole strip opens Desktop GPUs.
 */
export function DesktopStrip({
  view,
  href = '/admin/desktop-gpus',
  Link = PlainLink,
}: {
  view: AdminDesktopGpus;
  href?: string;
  Link?: LinkComponent | undefined;
}): ReactNode {
  return (
    <Link href={href} className="ds-strip">
      <span className="ds-strip__title">Desktop GPUs</span>
      <ul className="ds-strip__chips">
        {view.machines.map((m) => (
          <Chip key={m.worker} m={m} />
        ))}
        {view.neverConnected.map((e) => (
          <li key={e.note} className="ds-chip ds-chip--never">
            <span className="ds-chip__dot" aria-hidden="true" />
            <span className="ds-chip__text">
              <strong className="ds-chip__name">{e.note}</strong>
              <span className="ds-chip__state">Enrolled, never connected</span>
            </span>
          </li>
        ))}
        {view.machines.length === 0 && view.neverConnected.length === 0 ? (
          <li className="ds-chip ds-chip--never">No desktop GPU in 30 days</li>
        ) : null}
      </ul>
      <span className="ds-strip__open" aria-hidden="true">
        Open →
      </span>
    </Link>
  );
}
